import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { BookOpen, Plus, Heart, MessageCircle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function DigitalYearbook({ eventId, isOwner }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    author_name: "",
    title: "",
    message: "",
    photo_url: ""
  });
  const [commentData, setCommentData] = useState({});

  useEffect(() => {
    fetchEntries();
  }, [eventId]);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/yearbook`);
      setEntries(response.data);
    } catch (error) {
      console.error("Failed to fetch yearbook:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.author_name || !formData.message) {
      toast.error("Name und Nachricht sind erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/yearbook`,
        formData
      );
      
      toast.success("Eintrag hinzugefügt!");
      setFormData({ author_name: "", title: "", message: "", photo_url: "" });
      fetchEntries();
    } catch (error) {
      console.error("Failed to add entry:", error);
      toast.error("Fehler beim Hinzufügen");
    }
  };

  const handleLike = async (entryId) => {
    try {
      await axios.post(`${API}/events/${eventId}/yearbook/${entryId}/like`);
      fetchEntries();
    } catch (error) {
      console.error("Failed to like:", error);
    }
  };

  const handleComment = async (entryId) => {
    const comment = commentData[entryId];
    if (!comment || !comment.trim()) return;

    const author = prompt("Dein Name:");
    if (!author) return;

    try {
      await axios.post(
        `${API}/events/${eventId}/yearbook/${entryId}/comment`,
        { author_name: author, comment_text: comment }
      );
      setCommentData({ ...commentData, [entryId]: "" });
      toast.success("Kommentar hinzugefügt!");
      fetchEntries();
    } catch (error) {
      console.error("Failed to comment:", error);
      toast.error("Fehler beim Kommentieren");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <BookOpen className="w-5 h-5 text-primary" />
          Digitales Jahrbuch
        </h3>
        <p className="text-sm text-muted-foreground">
          Teilt Fotos, Erinnerungen und Nachrichten
        </p>
      </div>

      {/* Add Entry Form */}
      <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4 bg-secondary/20">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Dein Name *</Label>
            <Input
              value={formData.author_name}
              onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
              placeholder="Name"
              required
            />
          </div>
          <div>
            <Label>Titel (optional)</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="z.B. Beste Erinnerung"
            />
          </div>
        </div>

        <div>
          <Label>Nachricht/Erinnerung *</Label>
          <Textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Teile deine Gedanken, Erinnerungen oder Wünsche..."
            rows={3}
            required
          />
        </div>

        <div>
          <Label className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            Foto-URL (optional)
          </Label>
          <Input
            type="url"
            value={formData.photo_url}
            onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
            placeholder="https://..."
          />
        </div>

        <Button type="submit" className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          Eintrag hinzufügen
        </Button>
      </form>

      {/* Entries List */}
      <div className="space-y-4">
        {entries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Das Jahrbuch ist noch leer</p>
            <p className="text-xs mt-1">Sei der Erste, der einen Eintrag macht!</p>
          </div>
        ) : (
          entries.map((entry) => (
            <div key={entry.id} className="bg-card border rounded-lg overflow-hidden">
              {entry.photo_url && (
                <div className="h-48 bg-secondary relative">
                  <img 
                    src={entry.photo_url} 
                    alt={entry.title || "Foto"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              <div className="p-4">
                <div className="mb-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      {entry.title && (
                        <h4 className="font-medium mb-1">{entry.title}</h4>
                      )}
                      <p className="text-sm font-medium text-primary">{entry.author_name}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{entry.message}</p>
                </div>

                {/* Like & Comment Actions */}
                <div className="flex items-center gap-4 pt-3 border-t">
                  <button
                    onClick={() => handleLike(entry.id)}
                    className="flex items-center gap-1 text-sm hover:text-primary transition-colors"
                  >
                    <Heart className="w-4 h-4" />
                    <span>{entry.likes || 0}</span>
                  </button>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MessageCircle className="w-4 h-4" />
                    <span>{entry.comments?.length || 0}</span>
                  </div>
                </div>

                {/* Comments */}
                {entry.comments && entry.comments.length > 0 && (
                  <div className="mt-3 pt-3 border-t space-y-2">
                    {entry.comments.map((comment, idx) => (
                      <div key={idx} className="text-sm bg-secondary/30 rounded p-2">
                        <p className="font-medium text-xs text-primary">{comment.author_name}</p>
                        <p className="text-foreground">{comment.comment_text}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Comment */}
                <div className="mt-3 flex gap-2">
                  <Input
                    placeholder="Kommentar schreiben..."
                    value={commentData[entry.id] || ""}
                    onChange={(e) => setCommentData({ ...commentData, [entry.id]: e.target.value })}
                    className="flex-1 h-9"
                  />
                  <Button 
                    size="sm" 
                    onClick={() => handleComment(entry.id)}
                    disabled={!commentData[entry.id]}
                  >
                    Senden
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}